import { useContext, useState } from 'react';
import { AppModal } from '../';
import { AppContext } from '../../providers/AppContext';
import FigureModel from "../../models/FigureModel.js";
import { uniqueNamesGenerator, adjectives, animals } from 'unique-names-generator';
import {
  StyledUploadModal,
  UploadModalContent,
  RemoveButton,
  DropOrUpload,
  PopupControls,
  PotrtaitAndName,
  UploadSection,
  Portrait
} from './UploadModal.styled';
import MintFigureView from "../../components/MintActionFigures/MintFigureView/MintFigureView.js";
import { FileUploader } from "react-drag-drop-files";
import { TextInput, IconButton } from '../';
import { v4 } from 'uuid';
import { useTokenAuth } from '../../hooks';
import { createAvatar, getUploadUrls } from '../../providers/BasejumpApiProvider';
import { DiscordSdkContext } from '../../providers/DiscordSdkProvider';

const UPLOAD_FILE_TYPES = ["VRM"];
const UPLOAD_FILE_TYPES_PORTRAIT = ["PNG"];
const DEFAULT_PORTRAIT_URL = 'https://d9mwibab5tete.cloudfront.net/assets-prod/default/avatar.png';

const UploadModal = ({ asset }) => {
  const controls = {};
  const [avatarName, setAvatarName] = useState(uniqueNamesGenerator({dictionaries: [adjectives, animals], style: 'upperCase'}));
  const [vrmUrl, setVrmUrl] = useState(null);
  const [vrmFile, setVrmFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { setOwnedBroadsiders, ownedBroadsiders, setShowUpload, setSelectedBroadsider, setErrorData } = useContext(AppContext);
  const { basejumpToken } = useTokenAuth();
  const { isDiscord } = useContext(DiscordSdkContext);

  const model = {
    id: v4(),
    name: avatarName,
    vrm: vrmUrl,
    glb: vrmUrl,
    image: imageUrl || '/assets/avatar.png'
  };

  const handleLoad = async () => {
    setIsLoading(true);
    try {
      const files = [
        { name: 'model.vrm', content_type: 'application/octet-stream' },
        { name: 'model.glb', content_type: 'application/octet-stream' },
        { name: 'model.png', content_type: 'image/png' }
      ];
      const urls = await getUploadUrls(basejumpToken, files);
      // Upload VRM
      const vrmUrlConfig = urls.find((u) => u.name === 'model.vrm');
      const vrmResult = await fetch(vrmUrlConfig.upload_url, {
        method: "PUT",
        body: vrmFile,
      });
      if (!vrmResult.ok) {
        throw new Error('Failed to upload VRM!');
      }
      // Upload GLB - same as VRM in fact
      const glbUrlConfig = urls.find((u) => u.name === 'model.glb');
      const glbResult = await fetch(glbUrlConfig.upload_url, {
        method: "PUT",
        body: vrmFile,
      });
      if (!glbResult.ok) {
        throw new Error('Failed to upload GLB!');
      }
      const avatarData = {
        name: avatarName,
        vrm_url: vrmUrlConfig.result_url,
        glb_url: glbUrlConfig.result_url,
        image_url: DEFAULT_PORTRAIT_URL
      };

      if (imageFile) {
      // Upload Image
        const imageUrlConfig = urls.find((u) => u.name === 'model.png');
        const imageResult = await fetch(imageUrlConfig.upload_url, {
          method: "PUT",
          body: imageFile,
        });
        if (!imageResult.ok) {
          throw new Error('Failed to upload image!');
        }
        avatarData.image_url = imageUrlConfig.result_url;
      }
      const avatar = await createAvatar(basejumpToken, avatarData);
      const figure = FigureModel.fromBasejumpAvatar(avatar, isDiscord);
      setOwnedBroadsiders([...ownedBroadsiders, figure]);
      setSelectedBroadsider(figure);
      setShowUpload(false);
    } catch (error) {
      console.error(error);
      setErrorData({ title: 'Error', description: 'Failed to upload your avatar!' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = async (file) => {
    const arraybuffer = await file.arrayBuffer();
    const blob = new Blob([arraybuffer], { type: 'model/gltf-binary' });
    setVrmUrl(URL.createObjectURL(blob));
    setVrmFile(file);
  };
  
  const handleChangeImage = async (file) => {
    const arraybuffer = await file.arrayBuffer();
    const blob = new Blob([arraybuffer], { type: file.type });
    setImageFile(file);
    setImageUrl(URL.createObjectURL(blob));
  };

  return (
    <AppModal>
      <StyledUploadModal>
        <UploadModalContent>
          <UploadSection>
	    <PotrtaitAndName>
	    <Portrait>
	      {imageUrl ? (
                <img className='upload-modal-portrait-uploader' src={imageUrl}/>
              ) : (
                <FileUploader types={UPLOAD_FILE_TYPES_PORTRAIT} handleChange={handleChangeImage} classes='upload-modal-portrait-uploader'>
                  <DropOrUpload><div style={{padding: '1rem'}}>Load Portrait here</div></DropOrUpload>
                </FileUploader>
	      )}
              <div className='upload-section-corner upload-section-corner-bl'/>
              <div className='upload-section-corner upload-section-corner-br'/>
	    </Portrait>
            <PopupControls>
              <TextInput initText={avatarName} placeholder='Avatar Name' onChange={(text) => setAvatarName(text)} />
	      <IconButton onClick={handleLoad} animated={true} disabled={isLoading || !vrmFile || !avatarName}>
                <div className='upload-modal-button-content'>
                  <div>{isLoading ? 'Uploading...' : 'Add Avatar'}</div>
                  <img style={{paddingBottom: '6px', width: '2rem', height: '2rem'}} src='/assets/add_broadsider.svg'/>
                </div>
              </IconButton>
              <div className='upload-section-corner upload-section-corner-tl'/>
              <div className='upload-section-corner upload-section-corner-tr'/>
            </PopupControls>
	    </PotrtaitAndName>
	    {imageUrl && <RemoveButton onClick={() => { setImageUrl(null); setImageFile(null); }}>
              <img src='/assets/equip_hover.svg'/>
	    </RemoveButton>}
            <div className='upload-section-corner upload-section-corner-br'/>
            <div className='upload-section-corner upload-section-corner-tr'/>
          </UploadSection>
          <UploadSection $hasLeftBorder={true}>
            {vrmUrl ? (
              <MintFigureView controls={controls}
                figure={FigureModel.fromAlchemyNft(model)}
                noDecorators
                noEquip
                width='20rem'
                height='100%'
                maxHeight={'100%'}
              />
	    ) : (
              <FileUploader types={UPLOAD_FILE_TYPES} handleChange={handleChange} classes='upload-modal-file-uploader'>
                <DropOrUpload><div style={{padding: '1rem'}}>Load VRM here</div></DropOrUpload>
              </FileUploader>
	    )}
	    {vrmUrl && <RemoveButton onClick={() => { setVrmUrl(null); setVrmFile(null); }}>
              <img src='/assets/equip_hover.svg'/>
	    </RemoveButton>}
            <div className='upload-section-corner upload-section-corner-bl'/>
            <div className='upload-section-corner upload-section-corner-tl'/>
          </UploadSection>
        </UploadModalContent>
      </StyledUploadModal>
    </AppModal>
  );
};

export default UploadModal;
